/**
 * Write-time money checks. Ledger math stays in ledger.ts; this rejects
 * documents that would make balances impossible to compute.
 */

import type { Booking, SupplierPayment, SupplierRefund, TripDocument } from "./schema.ts";

export function tripFinancialIssues(doc: TripDocument): string[] {
  const issues: string[] = [];
  for (const booking of doc.bookings) {
    if (booking.cost && booking.allocations.length > 0) {
      const sum = booking.allocations.reduce((total, row) => total + row.amountMinor, 0);
      if (sum !== booking.cost.amountMinor) {
        issues.push(`Shares on ${booking.title ?? booking.id} must add up to the booking amount.`);
      }
    }
    const payments = doc.supplierPayments.filter((row) => row.bookingId === booking.id);
    for (const payment of payments) {
      if (booking.cost && payment.credit.currency !== booking.cost.currency) {
        issues.push(`A payment on ${booking.title ?? booking.id} is not in ${booking.cost.currency}.`);
      }
      const fundedSum = payment.fundedAllocations.reduce((total, row) => total + row.amountMinor, 0);
      if (fundedSum !== payment.credit.amountMinor) {
        issues.push(`Funded shares on a payment for ${booking.title ?? booking.id} must match the credited amount.`);
      }
    }
    const refunds = doc.supplierRefunds.filter((row) => row.bookingId === booking.id);
    for (const refund of refunds) {
      if (booking.cost && refund.credit.currency !== booking.cost.currency) {
        issues.push(`A refund on ${booking.title ?? booking.id} is not in ${booking.cost.currency}.`);
      }
      if (refund.paymentId) {
        const payment = payments.find((row) => row.id === refund.paymentId);
        if (!payment) {
          issues.push(`A refund on ${booking.title ?? booking.id} does not match a payment on that booking.`);
        }
      }
    }
  }
  return issues;
}

export function bookingHasFinancialHistory(doc: TripDocument, bookingId: string): boolean {
  return doc.supplierPayments.some((row) => row.bookingId === bookingId)
    || doc.supplierRefunds.some((row) => row.bookingId === bookingId);
}

export function refundableRemainder(payment: SupplierPayment, refunds: SupplierRefund[]): number {
  const used = refunds
    .filter((row) => row.paymentId === payment.id)
    .reduce((total, row) => total + row.credit.amountMinor, 0);
  return payment.credit.amountMinor - used;
}

export function validateRefundInput(
  doc: TripDocument,
  input: Pick<SupplierRefund, "bookingId" | "paymentId" | "credit" | "reversedAllocations">,
): string | null {
  const booking = doc.bookings.find((row) => row.id === input.bookingId);
  if (!booking?.cost) return "This booking has no cost to refund.";
  if (input.credit.amountMinor <= 0) return "Enter a positive refund amount.";
  if (booking.cost.currency !== input.credit.currency) return "Refund currency must match the booking.";
  if (!input.paymentId) return "Say which payment this refund reverses.";
  const payment = doc.supplierPayments.find((row) => row.id === input.paymentId);
  if (!payment || payment.bookingId !== booking.id) {
    return "That payment does not belong to this booking.";
  }
  if (payment.credit.currency !== input.credit.currency) {
    return "Refund currency must match the original payment.";
  }
  const remaining = refundableRemainder(payment, doc.supplierRefunds);
  if (input.credit.amountMinor > remaining) {
    return "That is more than remains refundable on this payment.";
  }
  if (input.reversedAllocations.length > 0) {
    const sum = input.reversedAllocations.reduce((total, row) => total + row.amountMinor, 0);
    if (sum !== input.credit.amountMinor) {
      return "Reversed household shares must add up to the refund amount.";
    }
  }
  return null;
}

export function currencyChangeBlocked(booking: Booking, nextCurrency: string, hasHistory: boolean): string | null {
  if (!hasHistory || !booking.cost) return null;
  if (booking.cost.currency === nextCurrency) return null;
  return "Currency cannot change after a payment or refund is recorded.";
}
